import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { Reveal, SectionHeading } from "./common";
import { GALLERY } from "../data/content";

export const Galleria = () => {
  const [index, setIndex] = useState(null);
  const open = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + GALLERY.length) % GALLERY.length),
    []
  );
  const next = useCallback(() => setIndex((i) => (i + 1) % GALLERY.length), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, prev, next]);

  return (
    <section id="galleria" className="relative bg-sand py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <SectionHeading
            eyebrow="Capitolo VI"
            title="Galleria"
            testid="section-galleria-title"
          />
          <Reveal delay={0.15}>
            <p className="mt-8 text-base sm:text-lg text-taupe leading-relaxed">
              Un percorso visivo attraverso la Basilica: la facciata, il rosone,
              i portali, l'interno, gli affreschi, il coro, i dettagli in pietra
              e il chiostro. Tocca un'immagine per ingrandirla.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:balance]">
          {GALLERY.map((item, i) => (
            <Reveal key={item.title} delay={(i % 3) * 0.06} className="mb-5 break-inside-avoid">
              <button
                onClick={() => setIndex(i)}
                data-testid={`gallery-item-${i}`}
                className="img-frame group relative block w-full text-left border border-stone hover:border-gold transition-colors duration-500"
                aria-label={`Apri ${item.title} nella galleria`}
              >
                <img
                  src={item.image}
                  alt={`${item.title} — ${item.caption}`}
                  className="w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-serif text-xl font-semibold text-ivory">
                        {item.title}
                      </h3>
                      <p className="text-xs text-ivory/75 mt-0.5">{item.caption}</p>
                    </div>
                    <Expand size={18} className="text-goldlight flex-shrink-0" />
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-8 text-xs sm:text-sm text-fog font-mono tracking-wide text-center">
            Fotografie d'archivio a scopo illustrativo (Unsplash · Pexels), scelte
            per coerenza con l'atmosfera romanico-gotica della Basilica
          </p>
        </Reveal>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[95] bg-ink/95 flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="lightbox"
          >
            <div className="flex items-center justify-between px-5 sm:px-8 h-[72px] flex-shrink-0">
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-goldlight">
                {index + 1} / {GALLERY.length} — {GALLERY[index].title}
              </span>
              <button
                onClick={close}
                data-testid="lightbox-close"
                aria-label="Chiudi la galleria"
                className="flex items-center justify-center w-11 h-11 text-ivory hover:text-goldlight transition-colors"
              >
                <X size={26} />
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center px-4 sm:px-16 pb-4 min-h-0 relative">
              <button
                onClick={prev}
                data-testid="lightbox-prev"
                aria-label="Immagine precedente"
                className="absolute left-3 sm:left-6 z-10 flex items-center justify-center w-12 h-12 rounded-full bg-ivory/10 text-ivory hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                <ChevronLeft size={22} />
              </button>
              <AnimatePresence mode="wait">
                <motion.img
                  key={index}
                  src={GALLERY[index].image}
                  alt={`${GALLERY[index].title} — ${GALLERY[index].caption}`}
                  className="max-h-full max-w-full object-contain shadow-2xl"
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                />
              </AnimatePresence>
              <button
                onClick={next}
                data-testid="lightbox-next"
                aria-label="Immagine successiva"
                className="absolute right-3 sm:right-6 z-10 flex items-center justify-center w-12 h-12 rounded-full bg-ivory/10 text-ivory hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                <ChevronRight size={22} />
              </button>
            </div>
            <div className="px-5 sm:px-8 pb-8 text-center flex-shrink-0">
              <p className="font-serif text-xl text-ivory">{GALLERY[index].title}</p>
              <p className="text-sm text-ivory/60 mt-1">{GALLERY[index].caption}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
